import { Controller } from "@hotwired/stimulus"

// Characters left at which a network's count appears under the field.
const BUDGET_WARN_AT = 40

// Share composer: the federation persona picker only applies to PUBLIC posts
// (only those leave this server), so it stays hidden until Public is chosen —
// keeping a single audience dropdown in the common case. The persona target is
// optional; it only exists for a federating user with >1 persona.
//
// The body field also supports a #hashtag typeahead: typing "#des" opens a
// dropdown (GET /apps/share/hashtags) of tags already used across the viewer's
// feed and picking one splices plain "#tag " text into the field. This mirrors
// the @-mention typeahead in community_chat_controller.js (same menu markup and
// key handling); the only difference is hashtags are plain text, not a mention
// token, since the reflection body is rendered by the linkify helper.
export default class extends Controller {
    static targets = ["audience", "persona", "input", "audienceChip", "audienceButton", "chipNetworks",
                      "destinations", "intent", "publishAt", "scheduleRow", "scheduleInput", "send",
                      "linkPreview", "linkThumb", "linkTitle", "linkSite",
                      "mediaType", "mediaUrl", "mediaTitle", "mediaThumb",
                      "budgets", "variantRow", "variantInput", "variantLabel", "variantCount", "variantMenuItem"]
    static values = { networks: Array }

    connect() {
        // Paint the chip's destination icons from whatever the server rendered
        // as checked, so a fresh (or turbo-replaced) composer is correct before
        // anyone touches it.
        this.toggleDestination()
        this._tag = null
        this._tagDebounce = null
        this._tagMenu = null
        this._sync()
    }

    disconnect() {
        this._closeTag()
    }

    // Bound from the chip picker's menu items (split-menu#choose closes the
    // menu itself). Repaints the chip with the picked label — the option
    // labels already carry their emoji, so this is just a verbatim copy —
    // writes the hidden field the form actually submits, then runs the same
    // reveal logic the old <select>'s change event used to trigger.
    pickAudience(event) {
        const { token, label } = event.currentTarget.dataset
        if (!token) return
        if (this.hasAudienceTarget) this.audienceTarget.value = token
        // The chip shows the words; the menu rows keep their emoji.
        if (this.hasAudienceChipTarget) this.audienceChipTarget.textContent = label.replace(/^[^\p{L}\p{N}]+/u, "")
        this._markActiveAudience(event.currentTarget)
        this._sync()
    }

    // Move the is-active marker (and its checkmark, via the
    // :not(.is-active) CSS rule) onto whichever row was just picked. Scoped
    // to this menu's own panel so it can't reach into an unrelated menu.
    _markActiveAudience(selected) {
        const panel = selected.closest(".assistant-header-menu-panel")
        if (!panel) return
        panel.querySelectorAll(".assistant-header-menu-item").forEach((item) => {
            item.classList.toggle("is-active", item === selected)
        })
    }

    // Kept for any other trigger that still fires a plain "change" against
    // the audience target (e.g. tests or future non-chip callers).
    audienceChanged() {
        this._sync()
    }

    _sync() {
        const isPublic = this.hasAudienceTarget && this.audienceTarget.value === "public"
        if (this.hasPersonaTarget) this.personaTarget.hidden = !isPublic
        // Destinations only apply to a public post — nothing else leaves this
        // server. Narrowing the audience also CLEARS the picked destinations,
        // rather than hiding them still checked: a hidden-but-checked box that
        // silently starts publishing again if the author flips back to Public
        // is exactly the kind of surprise a publishing tool must not have.
        // (The server enforces the same rule — see
        // ReflectionsController#publishing_params — this just keeps the UI
        // honest about it.)
        if (this.hasDestinationsTarget) {
            this.destinationsTarget.hidden = !isPublic
            if (!isPublic) {
                this.destinationsTarget.querySelectorAll("input[type=checkbox]").forEach((box) => { box.checked = false })
            }
        }
        this.toggleDestination()
        this._renderBudgets()
    }

    // Repaints the chip's destination icons. The chip carries the summary so
    // the choice stays visible without a second row of pills competing with it:
    // "Public" alone means Jane only, "Public ☁ 🌐" means it also leaves for
    // those two.
    toggleDestination() {
        this._renderBudgets()
        this._describeDestinations()
        if (!this.hasChipNetworksTarget) return

        const icons = this.hasDestinationsTarget && !this.destinationsTarget.hidden
            ? Array.from(this.destinationsTarget.querySelectorAll(".composer-network-input:checked"))
                .map((box) => box.closest(".composer-network")?.querySelector("i.bi")?.className)
                .filter(Boolean)
            : []

        this.chipNetworksTarget.replaceChildren(...icons.map((className) => {
            const icon = document.createElement("i")
            icon.className = className
            icon.setAttribute("aria-hidden", "true")
            return icon
        }))
        this.chipNetworksTarget.hidden = icons.length === 0
    }

    // The chip carries only the audience; where else a public post goes is
    // named in its tooltip (and listed in its menu).
    _describeDestinations() {
        if (!this.hasAudienceButtonTarget) return
        const names = this.hasDestinationsTarget && !this.destinationsTarget.hidden
            ? Array.from(this.destinationsTarget.querySelectorAll(".composer-network-input:checked"))
                .map((box) => box.closest(".composer-network")?.querySelector("span")?.textContent?.trim())
                .filter(Boolean)
            : []
        this.audienceButtonTarget.title = names.length
            ? `Who can see this. Also posting to ${names.join(", ")}.`
            : "Who can see this, and where else it goes"
    }

    // The field grows with what you write (one line to start), up to the
    // stylesheet's max-height, like X's and LinkedIn's composers.
    _autosize() {
        if (!this.hasInputTarget) return
        const field = this.inputTarget
        field.style.height = "auto"
        field.style.height = `${field.scrollHeight}px`
    }

    // --- Posting intent: post now / save as draft / schedule ----------------
    //
    // The form is a plain submit, so "intent" is just a hidden field the
    // server reads. With JS off nothing here runs and the default value
    // ("publish") is what posts — the composer degrades to exactly what it was
    // before this existed.

    saveDraft() {
        this._setIntent("draft")
        this.element.requestSubmit()
    }

    openSchedule() {
        if (!this.hasScheduleRowTarget) return
        this.scheduleRowTarget.hidden = false
        if (this.hasScheduleInputTarget) {
            // Default to an hour out, rounded to the next five minutes —
            // "schedule" almost never means "in thirty seconds", and an empty
            // picker makes the author do arithmetic before they can proceed.
            if (!this.scheduleInputTarget.value) {
                const at = new Date(Date.now() + 60 * 60 * 1000)
                at.setMinutes(Math.ceil(at.getMinutes() / 5) * 5, 0, 0)
                this.scheduleInputTarget.value = this._localInputValue(at)
            }
            this.scheduleInputTarget.focus()
        }
        this.setSchedule()
    }

    setSchedule() {
        if (!this.hasScheduleInputTarget) return
        const value = this.scheduleInputTarget.value
        if (!value) return this.clearSchedule()

        this._setIntent("schedule")
        if (this.hasPublishAtTarget) this.publishAtTarget.value = value
        this._relabelSend("Schedule")
    }

    clearSchedule() {
        this._setIntent("publish")
        if (this.hasPublishAtTarget) this.publishAtTarget.value = ""
        if (this.hasScheduleInputTarget) this.scheduleInputTarget.value = ""
        if (this.hasScheduleRowTarget) this.scheduleRowTarget.hidden = true
        this._relabelSend("Share")
    }

    _setIntent(value) {
        if (this.hasIntentTarget) this.intentTarget.value = value
    }

    // The primary button keeps its arrow but says what it will actually do —
    // pressing "Share" and getting a scheduled post would be a lie.
    _relabelSend(label) {
        if (!this.hasSendTarget) return
        this.sendTarget.title = label
        this.sendTarget.setAttribute("aria-label", label)
    }

    // datetime-local wants "YYYY-MM-DDTHH:MM" in LOCAL wall-clock time.
    // toISOString() would hand it UTC and shift the default by the user's
    // offset, so the parts are read off the local Date directly.
    _localInputValue(date) {
        const pad = (n) => String(n).padStart(2, "0")
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
    }

    // --- Autocomplete: #hashtags and @mentions -------------------------------
    //
    // Detects a "#query" or "@query" fragment before the caret, fetches
    // candidates, and on pick splices the plain token back into the field.
    // Bound from the view: input -> onInput, keydown -> onKeydown, blur ->
    // onBlur.
    //
    // One state machine for both, keyed on `kind`, rather than two parallel
    // copies of the same 120 lines of caret/keyboard/menu handling. Only three
    // things differ: the detect pattern, the endpoint, and what a picked row
    // inserts.
    //
    // A mention inserts PLAIN "@handle" text, not a bracketed token — these
    // posts federate, and Community's `@[Name](user:123)` scheme would arrive
    // on Bluesky as literal garbage. See Reflections::MentionCandidates.

    onInput() {
        if (!this.hasInputTarget) return
        this._autosize()
        this._detectTag()
        this._detectLink()
        this._renderBudgets()
    }

    // --- Per-network character budgets --------------------------------------
    //
    // Jane takes 5,000 characters. Bluesky truncates at 300 GRAPHEMES and
    // appends a permalink; Mastodon's default is 500. The composer showed none
    // of those numbers, so a 400-character post arrived on Bluesky as a stub
    // and the author had no way to know until they looked.
    //
    // Counted in graphemes, not characters: an emoji or an accented letter
    // built from combining codepoints is ONE grapheme and several chars, and
    // counting chars would warn on posts that are actually fine.

    _graphemes(text) {
        if (!this._segmenter) {
            this._segmenter = typeof Intl !== "undefined" && Intl.Segmenter
                ? new Intl.Segmenter(undefined, { granularity: "grapheme" })
                : null
        }
        // \r is stripped before counting: a textarea reports CRLF, the server
        // normalises it away (Reflection#normalize_newlines), so counting it
        // here would quietly understate the budget by one per line break.
        const clean = text.replace(/\r/g, "")
        // Array.from splits by code POINT, which is already far closer than
        // .length (that counts UTF-16 units and double-counts every emoji).
        return this._segmenter ? [...this._segmenter.segment(clean)].length : Array.from(clean).length
    }

    _renderBudgets() {
        if (!this.hasBudgetsTarget) return
        const chosen = this._chosenNetworks()
        this.budgetsTarget.hidden = chosen.length === 0
        if (!chosen.length) {
            if (this.hasVariantMenuItemTarget) this.variantMenuItemTarget.hidden = true
            return
        }
        if (this.hasVariantMenuItemTarget) this.variantMenuItemTarget.hidden = false

        // Only the networks you are close to (or over): a row of full
        // allowances on an empty post is noise, the way X shows its counter
        // only as you near 280.
        const near = chosen
            .map((net) => ({ ...net, left: net.limit - this._graphemes(this._textFor(net.key)) }))
            .filter((net) => net.left <= BUDGET_WARN_AT)
        this.budgetsTarget.hidden = near.length === 0
        this.budgetsTarget.replaceChildren(...near.map((net) => {
            const left = net.left
            const chip = document.createElement("button")
            chip.type = "button"
            chip.className = `composer-budget${left < 0 ? " composer-budget--over" : ""}`
            chip.dataset.network = net.key
            chip.dataset.action = "reflection-composer#openVariant"
            // Over-limit says what will actually HAPPEN, not just that a number
            // went red — Bluesky truncates and appends a link to the full post.
            chip.title = left < 0
                ? `${net.label}: ${-left} over. It will be cut short with a link back to the full post — click to write a shorter version.`
                : `${net.label}: ${left} left`
            chip.textContent = `${net.label} ${left}`
            return chip
        }))
    }

    _chosenNetworks() {
        if (!this.hasDestinationsTarget || this.destinationsTarget.hidden) return []
        const limits = this.hasNetworksValue ? this.networksValue : []
        return Array.from(this.destinationsTarget.querySelectorAll(".composer-network-input:checked"))
            .map((box) => limits.find((n) => n.key === box.value))
            .filter((n) => n && n.limit)
    }

    // What a given network will actually receive: its rewrite if there is one,
    // else the canonical body.
    _textFor(key) {
        const variant = this._variantField(key)
        return (variant && variant.value.trim()) || this.inputTarget.value
    }

    _variantField(key) {
        return this.element.querySelector(`[data-reflection-composer-variant="${key}"]`)
    }

    // --- Writing a different version for one network -------------------------

    openVariant(event) {
        this._showVariant(event.currentTarget.dataset.network)
    }

    // From the send menu, when you want a rewrite without being over a limit.
    openVariantMenu() {
        const first = this._chosenNetworks()[0]
        if (first) this._showVariant(first.key)
    }

    _showVariant(key) {
        const net = (this.hasNetworksValue ? this.networksValue : []).find((n) => n.key === key)
        if (!net || !this.hasVariantRowTarget) return

        this._variantKey = key
        this.variantLabelTarget.textContent = `The ${net.label} version`
        const field = this._variantField(key)
        this.variantInputTarget.value = field ? field.value : ""
        // Seeded from the canonical body so it is an EDIT, not a blank page —
        // the whole job is usually "trim this to fit".
        if (!this.variantInputTarget.value) this.variantInputTarget.value = this.inputTarget.value
        this.variantRowTarget.hidden = false
        this.variantInputTarget.focus()
        this.onVariantInput()
    }

    onVariantInput() {
        if (!this._variantKey) return
        const field = this._variantField(this._variantKey)
        // A rewrite identical to the canonical body is not a rewrite; storing
        // it would give the two copies a chance to drift apart later.
        const text = this.variantInputTarget.value
        if (field) field.value = text.trim() === this.inputTarget.value.trim() ? "" : text

        const net = (this.hasNetworksValue ? this.networksValue : []).find((n) => n.key === this._variantKey)
        if (net && this.hasVariantCountTarget) {
            const left = net.limit - this._graphemes(text)
            this.variantCountTarget.textContent = `${left} left`
            this.variantCountTarget.classList.toggle("is-over", left < 0)
        }
        this._renderBudgets()
    }

    closeVariant() {
        if (this._variantKey) {
            const field = this._variantField(this._variantKey)
            if (field) field.value = ""
        }
        this._variantKey = null
        if (this.hasVariantRowTarget) this.variantRowTarget.hidden = true
        this._renderBudgets()
    }

    // --- Link previews -------------------------------------------------------
    //
    // Unfurls the FIRST URL in the body, debounced, and shows the card before
    // you post. Dismissing it is sticky for that URL: re-typing does not bring
    // back a card someone deliberately removed, which is the behaviour every
    // other composer has and the one people expect.

    _detectLink() {
        // A reply composer mounts this controller for the typeaheads but
        // renders no preview card — and a reply carries no media_* columns to
        // put one in. Bail before spending a request that has nowhere to go.
        if (!this.hasLinkPreviewTarget) return

        const match = this.inputTarget.value.match(/https?:\/\/[^\s]+/)
        const url = match ? match[0].replace(/[.,;:!?)\]]+$/, "") : null

        if (!url) {
            // The URL is gone from the body, so the card that described it
            // should go too — but a dismissal is remembered per URL, not
            // cleared here, or deleting and retyping would resurrect it.
            if (this._previewUrl) this._clearPreview()
            return
        }
        if (url === this._previewUrl || url === this._dismissedUrl) return

        clearTimeout(this._previewDebounce)
        this._previewDebounce = setTimeout(() => this._fetchPreview(url), 500)
    }

    async _fetchPreview(url) {
        try {
            const res = await fetch(`/apps/share/preview?url=${encodeURIComponent(url)}`, {
                headers: { Accept: "application/json" }
            })
            if (!res.ok) return
            const payload = await res.json()
            // The body may have moved on while this was in flight.
            if (!payload.ok || this._dismissedUrl === url) return
            if (!this.inputTarget.value.includes(url)) return
            this._showPreview(url, payload.preview)
        } catch (e) {
            // A preview is a nicety — a failure leaves a plain link.
        }
    }

    _showPreview(url, card) {
        this._previewUrl = url
        if (this.hasMediaTypeTarget) this.mediaTypeTarget.value = "article"
        if (this.hasMediaUrlTarget) this.mediaUrlTarget.value = card.url || url
        if (this.hasMediaTitleTarget) this.mediaTitleTarget.value = card.title || ""
        if (this.hasMediaThumbTarget) this.mediaThumbTarget.value = card.image_url || ""

        if (this.hasLinkTitleTarget) this.linkTitleTarget.textContent = card.title || url
        if (this.hasLinkSiteTarget) this.linkSiteTarget.textContent = card.site_name || ""
        if (this.hasLinkThumbTarget) {
            this.linkThumbTarget.hidden = !card.image_url
            if (card.image_url) this.linkThumbTarget.src = card.image_url
        }
        if (this.hasLinkPreviewTarget) this.linkPreviewTarget.hidden = false
    }

    dismissLinkPreview() {
        this._dismissedUrl = this._previewUrl
        this._clearPreview()
    }

    _clearPreview() {
        this._previewUrl = null
        if (this.hasLinkPreviewTarget) this.linkPreviewTarget.hidden = true
        ;[this.hasMediaTypeTarget && this.mediaTypeTarget,
          this.hasMediaUrlTarget && this.mediaUrlTarget,
          this.hasMediaTitleTarget && this.mediaTitleTarget,
          this.hasMediaThumbTarget && this.mediaThumbTarget].forEach((f) => { if (f) f.value = "" })
    }

    onKeydown(event) {
        // While the dropdown is open it owns Arrow/Enter/Escape/Tab so the
        // keystroke picks a tag instead of moving the caret / inserting a newline.
        if (!this._tag) return
        if (event.key === "Escape") { event.preventDefault(); this._closeTag(); return }
        const items = this._tag.items || []
        if (event.key === "ArrowDown") { event.preventDefault(); this._moveTag(1); return }
        if (event.key === "ArrowUp") { event.preventDefault(); this._moveTag(-1); return }
        if ((event.key === "Enter" || event.key === "Tab") && items.length && this._tag.index >= 0) {
            event.preventDefault()
            this._pickTag(this._tag.index)
        }
    }

    onBlur() {
        // Delay so a mousedown on a menu row wins over the blur that dismisses.
        setTimeout(() => this._closeTag(), 120)
    }

    _detectTag() {
        const input = this.inputTarget
        const caret = input.selectionStart
        if (caret === null || caret === undefined) return this._closeTag()
        const before = input.value.slice(0, caret)

        // Preceded by start-of-string or whitespace. A hashtag query can hold
        // no second "#"; a mention query CAN hold an "@" (a fediverse handle is
        // "user@host"), so it only stops at whitespace.
        let kind = null
        let match = before.match(/(?:^|\s)#([^\s#]*)$/)
        if (match) {
            kind = "tag"
        } else {
            match = before.match(/(?:^|\s)@([^\s]*)$/)
            if (match) kind = "mention"
        }
        if (!kind) return this._closeTag()

        const query = match[1]
        const same = this._tag && this._tag.query === query && this._tag.kind === kind
        this._tag = {
            kind,
            query,
            start: caret - query.length - 1, // index of the "#" or "@"
            end: caret,
            items: same ? this._tag.items : [],
            index: same ? this._tag.index : -1
        }
        this._ensureTagMenu()
        this._positionTagMenu()
        this._renderTagItems()
        this._scheduleTagFetch(query)
    }

    _scheduleTagFetch(query) {
        if (this._tagDebounce) clearTimeout(this._tagDebounce)
        const kind = this._tag && this._tag.kind
        this._tagDebounce = setTimeout(() => this._fetchTags(query, kind), 180)
    }

    async _fetchTags(query, kind) {
        const path = kind === "mention" ? "/apps/share/mentions" : "/apps/share/hashtags"
        try {
            const res = await fetch(`${path}?q=${encodeURIComponent(query)}`, {
                headers: { Accept: "application/json" }
            })
            if (!res.ok) throw new Error("candidates failed")
            const payload = await res.json()
            // The caret may have moved on (or switched kinds) while this was in
            // flight; dropping a stale response is what stops the menu showing
            // people for a hashtag query.
            if (!this._tag || this._tag.query !== query || this._tag.kind !== kind) return
            this._tag.items = (kind === "mention" ? payload.people : payload.tags) || []
            this._tag.index = this._tag.items.length ? 0 : -1
            this._renderTagItems()
        } catch (e) {
            this._closeTag()
        }
    }

    _ensureTagMenu() {
        if (this._tagMenu) return
        const menu = document.createElement("div")
        menu.className = "tasks-mention-menu"
        document.body.appendChild(menu)
        this._tagMenu = menu
    }

    _renderTagItems() {
        const menu = this._tagMenu
        if (!menu || !this._tag) return
        const items = this._tag.items || []
        menu.innerHTML = ""
        if (!items.length) { menu.hidden = true; return }
        menu.hidden = false
        items.forEach((tag, index) => {
            const row = document.createElement("button")
            row.type = "button"
            row.className = "tasks-mention-item"
            row.classList.toggle("is-active", index === this._tag.index)

            // A mention row shows a face and the handle underneath the name —
            // two people can share a display name, and the handle is what
            // actually gets typed into the post.
            if (this._tag.kind === "mention" && tag.avatar_url) {
                const avatar = document.createElement("img")
                avatar.className = "tasks-mention-item-avatar"
                avatar.src = tag.avatar_url
                avatar.alt = ""
                avatar.referrerPolicy = "no-referrer"
                row.appendChild(avatar)
            }

            const name = document.createElement("span")
            name.className = "tasks-mention-item-name"
            name.textContent = tag.name || `#${tag.token}`
            row.appendChild(name)

            const metaText = this._tag.kind === "mention" ? `@${tag.handle}` : tag.count
            if (metaText) {
                const meta = document.createElement("span")
                meta.className = "tasks-mention-item-meta"
                meta.textContent = metaText
                row.appendChild(meta)
            }
            row.addEventListener("mousedown", (ev) => { ev.preventDefault(); this._pickTag(index) })
            row.addEventListener("mouseenter", () => this._setTagIndex(index))
            menu.appendChild(row)
        })
    }

    _positionTagMenu() {
        const menu = this._tagMenu
        if (!menu || !this.hasInputTarget) return
        const rect = this.inputTarget.getBoundingClientRect()
        menu.style.left = `${rect.left}px`
        menu.style.width = `${Math.max(rect.width, 220)}px`
        // Open downward: the composer sits at the top of the feed.
        menu.style.top = `${rect.bottom + 6}px`
        menu.style.bottom = "auto"
    }

    _moveTag(delta) {
        const items = (this._tag && this._tag.items) || []
        if (!items.length) return
        let next = this._tag.index + delta
        if (next < 0) next = items.length - 1
        if (next >= items.length) next = 0
        this._setTagIndex(next)
    }

    _setTagIndex(index) {
        if (!this._tag) return
        this._tag.index = index
        if (!this._tagMenu) return
        Array.from(this._tagMenu.children).forEach((row, i) => row.classList.toggle("is-active", i === index))
    }

    _pickTag(index) {
        const t = this._tag
        if (!t) return
        const item = (t.items || [])[index]
        if (!item) { this._closeTag(); return }

        let inserted
        if (t.kind === "mention") {
            const handle = String(item.handle || "")
            // Same shape the server's MENTION_PATTERN will match when the body
            // renders — anything else would insert text that never becomes a
            // link.
            if (!/^[A-Za-z0-9][A-Za-z0-9._-]*[A-Za-z0-9](@[A-Za-z0-9.-]+)?$/.test(handle)) { this._closeTag(); return }
            inserted = `@${handle} `
        } else {
            const token = String(item.token || "").replace(/^#/, "")
            if (!/^\w+$/.test(token)) { this._closeTag(); return }
            inserted = `#${token} `
        }

        const input = this.inputTarget
        const value = input.value
        // Plain text (trailing space so the next word is clear); the linkify
        // helper turns it into a link when the body renders.
        input.value = value.slice(0, t.start) + inserted + value.slice(t.end)
        const pos = t.start + inserted.length
        input.setSelectionRange(pos, pos)
        input.focus()
        this._closeTag()
    }

    _closeTag() {
        if (this._tagDebounce) { clearTimeout(this._tagDebounce); this._tagDebounce = null }
        this._tag = null
        if (this._tagMenu) {
            this._tagMenu.remove()
            this._tagMenu = null
        }
    }
}
